/**
 * Copy text to the clipboard with a mobile/webview fallback.
 *
 * `navigator.clipboard.writeText` is flaky in mobile in-app browsers (the
 * Twitter / Telegram / Gmail webviews a lot of our traffic arrives from) and is
 * simply absent outside a secure context — where it silently no-ops. Copying the
 * contract address is the #1 micro-action on the token page, and this was the
 * top rage-click target (86% mobile), so fall back to a hidden-textarea
 * execCommand('copy'), which works in far more environments.
 *
 * Returns whether the copy succeeded so the caller can show real feedback.
 */
export async function copyText(text: string): Promise<boolean> {
  // Preferred path — real browsers in a secure context.
  try {
    if (navigator.clipboard && window.isSecureContext) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    /* fall through to the legacy path */
  }
  // Legacy fallback — works in most in-app webviews and insecure contexts.
  try {
    const ta = document.createElement('textarea');
    ta.value = text;
    ta.setAttribute('readonly', '');
    ta.style.position = 'fixed';
    ta.style.top = '-9999px';
    ta.style.opacity = '0';
    document.body.appendChild(ta);
    ta.focus();
    ta.select();
    ta.setSelectionRange(0, text.length);
    const ok = document.execCommand('copy');
    document.body.removeChild(ta);
    return ok;
  } catch {
    return false;
  }
}
